/**
 * ENG-5641 — read-only HTTP connectivity probes for the direct-API ('http_provider')
 * integrations (Linear, Google Workspace, Xero, v0, Buffer).
 *
 * Centralized in core so BOTH consumers share one implementation (DRY):
 *   - the manager-CLI host-side probe executor, and
 *   - the API `POST /integrations/:id/test` endpoint (when it adopts the resolver).
 *
 * `fetch` is injected so this is unit-testable without network. Every probe is
 * a single read-only call (a `viewer`/`userinfo`/`connections`/`user` GET or a
 * GraphQL `viewer` query) — no mutations, ever.
 *
 * Outcome mapping (to the ConnectivityStatus vocabulary):
 *   - 'ok'              — reachable and the read returned a usable result
 *   - 'down'            — auth rejected (401, or a 403 that isn't throttling) or a
 *                         semantic dead-end (no viewer, no connected orgs) — needs attention
 *   - 'transient_error' — network/timeout, 5xx, or throttling (429, and a 403
 *                         carrying rate-limit headers) — retryable, don't escalate yet
 */

import type { ConnectivityProbeOutcome } from './connectivity-probe.js';
import type { ConnectivityCause } from './integration-health.js';
import { basicUsernameAuthorization } from './integration-broker/http-templater.js';

const PROBE_TIMEOUT_MS = 10_000;

interface HttpCreds {
  api_key?: string;
  access_token?: string;
  [k: string]: unknown;
}

/**
 * Statuses the Response constructor refuses to pair with a body. Re-wrapping one
 * with `''` throws a TypeError, which would turn a legitimate 204 into a probe
 * crash rather than a verdict.
 */
const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

/**
 * Fetch under a hard deadline that covers the BODY, not just the headers
 * (ENG-8502, CodeRabbit PR #4103).
 *
 * The original cleared the timer as soon as `fetchImpl` resolved, which is the
 * moment headers arrive — the body is still streaming at that point. A server
 * that answers with headers and then stalls the body left every probe here
 * awaiting `res.json()` with no deadline at all, pending indefinitely. The
 * `AbortController` was already the right mechanism; it was just being disarmed
 * one step too early.
 *
 * Fixed centrally rather than per-probe: all six call sites read a body
 * immediately after, so all six had the same exposure, and patching only the
 * one CodeRabbit happened to be reviewing would have left the other five
 * hanging on the same input.
 *
 * The body is buffered here, inside the deadline, and handed back as a
 * synthetic Response so callers keep their existing `.json()` / `.text()` API —
 * now reading from memory, so those awaits can no longer block. Safe because no
 * probe streams (`res.body`) or needs the original object identity.
 */
async function timedFetch(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetchImpl(url, { ...init, signal: controller.signal });
    if (NULL_BODY_STATUSES.has(res.status)) return res;
    const body = await res.text();
    return new Response(body, {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * True when a response's own headers say we were THROTTLED rather than rejected.
 *
 * Only ever consulted for a 403 (see {@link statusForHttp}) — a 429 needs no
 * corroboration, and a 401 is never a rate limit. Both signals are the
 * conventional ones:
 *   - `retry-after` is only sent when a server wants you to back off.
 *   - `x-ratelimit-remaining: 0` says the quota for this window is spent.
 *
 * Both signals reject a present-but-BLANK value, for the same reason: a blank
 * header is not evidence of throttling, but `'' !== null` is true and
 * `Number('')` is `0`, so either would otherwise wave a rejected 403 through as
 * `transient_error` — a dead credential that never escalates, which is the worse
 * of the two failure modes this file trades between.
 *
 * `retry-after` originally checked presence alone, so it had the very hole this
 * comment claimed to have closed for `x-ratelimit-remaining` (CodeRabbit, PR
 * #4479). Keep the two branches symmetric: a new signal added here needs the
 * same blank check, or it reopens it.
 */
function isRateLimited(res: Response): boolean {
  const retryAfter = res.headers.get('retry-after');
  if (retryAfter !== null && retryAfter.trim() !== '') return true;
  const remaining = res.headers.get('x-ratelimit-remaining');
  if (remaining === null || remaining.trim() === '') return false;
  return Number(remaining) === 0;
}

/**
 * Map a non-ok HTTP response to the right connectivity status.
 *
 * ENG-5463. **429 is a rate limit for every provider, never a credential
 * failure.** This used to funnel every non-5xx to `down`, and two providers
 * discovered that independently and each patched its own call site: Vercel
 * (ENG-8422) and Higgsfield (ENG-8502). Vercel's comment stated the general
 * problem and then deliberately declined to fix it centrally, on the grounds
 * that the other providers' rate-limit semantics were uncharacterised and
 * re-mapping them was a fleet-wide change that issue hadn't asked for.
 *
 * That reasoning has since expired, in both directions. RFC 6585 defines 429 as
 * "Too Many Requests" with no provider-specific reading available, so there is
 * nothing left to characterise. And the consequence got much more expensive:
 * `integration-connectivity-escalation` and `managed-health-connectivity-flip`
 * are now both armed, so a `down` runs the full ladder — consecutive-failure
 * counter, `integration_down` page to the team channel and agent owner, and a
 * sustained hard-down flipping the install to `needs_reauth`, which tells the
 * CUSTOMER to go reconnect a credential that never stopped working.
 *
 * `rateLimited` handles the harder case: **403 is ambiguous.** GitHub returns it
 * for secondary rate limits as well as for genuinely bad tokens, and Google for
 * quota exhaustion — so the status code alone cannot separate them and only the
 * headers can. It is opt-IN rather than the default because two providers have
 * their 403 semantics *verified against the live API*: Vercel 403s an invalid
 * token, and Higgsfield 403s a rejected key pair. For those, a header-driven
 * downgrade could read a dead credential as retryable and silently never
 * escalate it. A false `down` is loud and wrong; a false `transient_error` is
 * quiet and wrong, and quiet is worse here.
 */
// ENG-10574: exported so the MCP-over-HTTP arm grades the SAME response the same
// way. Two lanes answering "what does a 429 mean" independently is how they
// drifted: this one said `transient_error`, `mcp-http-probe.ts` said `down`.
export function statusForHttp(
  httpStatus: number,
  opts?: { rateLimited?: boolean },
): 'down' | 'transient_error' {
  if (httpStatus === 429) return 'transient_error';
  if (httpStatus === 403 && opts?.rateLimited) return 'transient_error';
  if (httpStatus === 401 || httpStatus === 403) return 'down';
  if (httpStatus >= 500) return 'transient_error';
  return 'down';
}

/**
 * ENG-9173 — the CAUSE for the same response {@link statusForHttp} graded.
 *
 * Deliberately adjacent, taking the identical inputs, because the two answers
 * must agree about the same response: a `transient_error` graded from a 429 and
 * a cause of anything but `rate_limited` would be a contradiction the
 * escalation layer then acts on. Keeping them side by side is what stops them
 * drifting — the branches are intentionally in the same order.
 *
 * WHY THIS HAS TO EXIST AT ALL. The status alone is not enough for the layer
 * above. 59 consecutive Buffer 429s on `basketball-media` were graded
 * `transient_error` (correct, ENG-5463) and then escalated to a CRITICAL page
 * naming no action anybody could take, because the escalation layer could not
 * tell a vendor throttle from a provider falling over. It could not tell because
 * the probe's message — which says "not a credential failure" in plain words —
 * is parsed at the report boundary and then dropped: there is no message column.
 * A structured cause is that fact made persistable.
 *
 * A 400/404/422 lands on `semantic`, not `unknown`: the request was
 * authenticated and reached the provider, and the provider rejected the ASK.
 * That is a different fix from a dead credential and should not be filed with
 * "we do not know".
 */
export function causeForHttp(
  httpStatus: number,
  opts?: { rateLimited?: boolean },
): ConnectivityCause {
  if (httpStatus === 429) return 'rate_limited';
  if (httpStatus === 403 && opts?.rateLimited) return 'rate_limited';
  if (httpStatus === 401 || httpStatus === 403) return 'auth_rejected';
  if (httpStatus >= 500) return 'server_error';
  return 'semantic';
}

/**
 * Operator-visible copy for a throttled probe, so the message matches the verdict.
 * `probeBearerJson` fronts five providers and has no name of its own, hence the
 * nameless form.
 */
function rateLimitMessage(provider: string | null, httpStatus: number): string {
  const subject = provider ? `${provider} rate limit` : 'Rate limited by the provider';
  return `${subject} (${httpStatus}) — not a credential failure`;
}

/**
 * Strip a credential out of a message before it becomes durable state.
 *
 * Every probe outcome's `message` is persisted (`status_message` /
 * `last_connectivity_status` on the install row) and rendered in the console, so
 * anything that reaches it is a durable, operator-visible record. The thrown-error
 * path is the one branch where we interpolate a string we did not author —
 * `Error.message` from whatever the fetch layer raised — so it is the one branch
 * that can carry a credential we never chose to print.
 *
 * The 4-char floor is deliberate: below that a value is not a credential, and
 * blind substring substitution would shred an otherwise useful diagnostic (a
 * 1-char "secret" would replace every occurrence of that letter). Every real
 * token format here — `vercel_…`, `gho_…`, `lin_api_…` — is far longer.
 */
function redactSecret(message: string, secret?: string): string {
  if (typeof secret !== 'string' || secret.length < 4) return message;
  return message.split(secret).join('[redacted]');
}

function networkOutcome(err: unknown, secret?: string): ConnectivityProbeOutcome {
  const isAbort = (err as Error)?.name === 'AbortError';
  const message = isAbort
    ? `Connection timed out after ${PROBE_TIMEOUT_MS / 1000}s`
    : `Connection failed: ${(err as Error).message}`;
  // ENG-9173 (CodeRabbit, PR #4750): `unreachable`, and this was a real gap — I
  // added the enum value and then did not set it on the ONE path it describes.
  // `causeForHttp` only runs when a response exists, so a timeout, a DNS failure
  // or any other fetch exception came through here with no cause and was
  // persisted as `unknown`.
  //
  // The consequence was not cosmetic: `unknown` is deliberately treated as
  // ACTIONABLE by the escalation layer, so every transport failure would have
  // kept escalating to CRITICAL while the two causes this change exists to
  // separate got their own handling. Half-classified is its own bug.
  //
  // `unreachable` covers our own timeouts as well as theirs, which is exactly
  // why `isVendorSideCause` excludes it — it stays actionable, and correctly so.
  // Naming it changes what an operator READS, not whether they are told.
  return { status: 'transient_error', cause: 'unreachable', message: redactSecret(message, secret) };
}

async function probeLinear(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // Linear accepts the API key in Authorization WITHOUT a Bearer prefix.
  const key = creds.api_key ?? creds.access_token;
  if (!key) return { status: 'down', message: 'No Linear credential present' };
  try {
    const res = await timedFetch(fetchImpl, 'https://api.linear.app/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: String(key) },
      body: JSON.stringify({ query: '{ viewer { id name email } }' }),
    });
    if (!res.ok) {
      const rateLimited = isRateLimited(res);
      const message =
        res.status === 429 || (res.status === 403 && rateLimited)
          ? rateLimitMessage('Linear', res.status)
          : `Linear API returned ${res.status}`;
      return {
        status: statusForHttp(res.status, { rateLimited }),
        cause: causeForHttp(res.status, { rateLimited }),
        message,
      };
    }
    const body = (await res.json()) as {
      data?: { viewer?: { name?: string; email?: string } };
      errors?: Array<{ message: string }>;
    };
    if (body.errors?.length) return { status: 'down', message: body.errors[0]?.message ?? 'Unknown Linear error' };
    const viewer = body.data?.viewer;
    if (!viewer) return { status: 'down', message: 'Invalid key — no viewer returned' };
    return { status: 'ok', message: `Connected as ${viewer.name ?? viewer.email ?? 'unknown'}` };
  } catch (err) {
    return networkOutcome(err, String(key ?? ''));
  }
}

async function probeBearerJson(
  url: string,
  creds: HttpCreds,
  fetchImpl: typeof fetch,
  interpret: (body: unknown) => ConnectivityProbeOutcome,
  // ENG-6100: some providers reject requests without extra headers — GitHub
  // 403s any request missing a `User-Agent`, which would otherwise be
  // misclassified as `down` (a false "reconnect required").
  extraHeaders?: Record<string, string>,
): Promise<ConnectivityProbeOutcome> {
  const token = creds.access_token ?? creds.api_key;
  if (!token) return { status: 'down', message: 'No credential present' };
  try {
    const res = await timedFetch(fetchImpl, url, { headers: { Authorization: `Bearer ${token}`, ...extraHeaders } });
    if (!res.ok) {
      // ENG-5463: GitHub returns 403 for secondary rate limits and Google for
      // quota exhaustion, so this shared helper — which fronts github,
      // google-workspace, xero, linkedin-ads and v0 — opts in to the
      // header-based 403 check. A bare 403 with no rate-limit headers still
      // reads `down`.
      const rateLimited = isRateLimited(res);
      const message =
        res.status === 401
          ? 'Token expired or revoked — reconnect required'
          : res.status === 429 || (res.status === 403 && rateLimited)
            ? rateLimitMessage(null, res.status)
            : `API returned ${res.status}`;
      return {
        status: statusForHttp(res.status, { rateLimited }),
        cause: causeForHttp(res.status, { rateLimited }),
        message,
      };
    }
    return interpret(await res.json());
  } catch (err) {
    return networkOutcome(err, String(token ?? ''));
  }
}

async function probeBuffer(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // ENG-6642: Buffer's GraphQL API authenticates with an API key sent as a
  // Bearer token. (Buffer rejects OIDC tokens for direct API calls — the API
  // key minted at publish.buffer.com/settings/api is the only accepted
  // credential.) The lightest authenticated read is the account's
  // organizations: it proves the key is valid AND that at least one Buffer
  // organization is reachable, which every other call needs.
  const key = creds.api_key ?? creds.access_token;
  if (!key) return { status: 'down', message: 'No Buffer credential present' };
  try {
    const res = await timedFetch(fetchImpl, 'https://api.buffer.com', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
      body: JSON.stringify({ query: '{ account { organizations { id name } } }' }),
    });
    if (!res.ok) {
      const rateLimited = isRateLimited(res);
      const message =
        res.status === 401
          ? // ENG-9809: name the mechanism, because the bare "expired or revoked" sent
            // two investigations at the wrong thing. Buffer API keys carry a MANDATORY
            // lifetime chosen at mint time — 7/30/60/90 days or 1 year, defaulting to
            // 30 days, with no "never expires" option
            // (support.buffer.com/article/984-how-to-create-your-buffer-api-key). So
            // every Buffer install on the fleet is on a countdown, and expiry is the
            // ordinary end of a key's life rather than a customer revoking one.
            //
            // All three causes are named, because this one branch answers every Buffer
            // 401 — a key that lapsed, one that was revoked, and one that was simply
            // mistyped ("Requests without a valid key will return a 401 Unauthorized",
            // developers.buffer.com/guides/authentication.html). The status code alone
            // cannot separate them, so naming expiry as the LIKELIER cause is supported;
            // asserting it is not, and a message that omitted "invalid" would send
            // someone who pasted the key wrong looking for an expiry date instead.
            'Buffer API key expired, invalid, or revoked — reconnect required. Buffer keys ' +
            'expire by design (30-day default, 1-year maximum), so on a key that was working ' +
            'until now expiry is the likeliest of the three; pick 1 year when minting the ' +
            'replacement.'
          : res.status === 429 || (res.status === 403 && rateLimited)
            ? rateLimitMessage('Buffer', res.status)
            : `Buffer API returned ${res.status}`;
      return {
        status: statusForHttp(res.status, { rateLimited }),
        cause: causeForHttp(res.status, { rateLimited }),
        message,
      };
    }
    const body = (await res.json()) as {
      data?: { account?: { organizations?: Array<{ id?: string; name?: string }> } };
      errors?: Array<{ message: string }>;
    };
    if (body.errors?.length) return { status: 'down', message: body.errors[0]?.message ?? 'Unknown Buffer error' };
    const orgs = body.data?.account?.organizations ?? [];
    if (!orgs.length) return { status: 'down', message: 'No Buffer organizations on this account' };
    return { status: 'ok', message: `Connected to ${orgs[0]?.name ?? 'Buffer'}` };
  } catch (err) {
    return networkOutcome(err, String(key ?? ''));
  }
}

async function probeVercel(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // ENG-8422. Vercel needs its own probe rather than `probeBearerJson` for
  // three reasons, each verified against the live API on 2026-08-04:
  //
  //  1. **Auth failures are 403, not 401.** Both a missing token
  //     (`{"error":{"code":"forbidden","missingToken":true}}`) and an invalid
  //     one (`{"error":{"code":"forbidden","invalidToken":true}}`) come back
  //     403. `probeBearerJson` only says "reconnect required" on 401, so Vercel
  //     would have produced a correct-but-mute `API returned 403`. (401 is also
  //     documented, so both are handled.) A 403 here means the TOKEN is bad, not
  //     that a scope is missing — every Vercel token can read its own user.
  //  2. **The success body is wrapped**: `{ user: { id, email, name, username } }`,
  //     with a second `limited: true` variant for tokens that can't read the full
  //     user record. `limited` is still an authenticated token, so it is green.
  //  3. **429 must not read as `down`** — the misreport ENG-8422 criterion 4
  //     rules out. This was originally handled inline here, deferring the
  //     fleet-wide fix; ENG-5463 moved it into `statusForHttp`, so the rule now
  //     lives in one place and a provider cannot silently miss it. Only the
  //     MESSAGE stays provider-specific.
  //
  //     Note this probe deliberately does NOT opt in to the 403 rate-limit
  //     check: per (1) above, a Vercel 403 is a VERIFIED bad token, so letting
  //     headers downgrade it to `transient_error` would mean a revoked token
  //     never escalates.
  const token = creds.api_key ?? creds.access_token;
  if (!token) return { status: 'down', message: 'No Vercel credential present' };
  try {
    const res = await timedFetch(fetchImpl, 'https://api.vercel.com/v2/user', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const message =
        res.status === 429
          ? rateLimitMessage('Vercel', res.status)
          : res.status === 401 || res.status === 403
            ? `Vercel rejected the token (${res.status}) — invalid or revoked API token`
            : `Vercel API returned ${res.status}`;
      return {
        status: statusForHttp(res.status),
        cause: causeForHttp(res.status),
        message,
      };
    }
    const body = (await res.json()) as {
      user?: { id?: string; username?: string; email?: string; name?: string | null };
    };
    const user = body?.user;
    // ENG-8358's lesson: a 200 carrying nothing must not read green. `id` is the
    // last-resort identity so a sparse-but-real user can't produce a false
    // `down` — only a genuinely empty/missing `user` does.
    //
    // First non-empty STRING, not `??`: nullish coalescing stops at `''`, so a
    // blank `username` would shadow a perfectly good `email`/`id` and report
    // `down` on a working token — the false-down this fallback chain exists to
    // avoid. The typeof check also keeps a malformed value (an object, a number)
    // from rendering as `Connected as [object Object]`.
    const identity = [user?.username, user?.email, user?.name, user?.id].find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0,
    );
    if (!identity) return { status: 'down', message: 'Vercel returned no user for this token' };
    return { status: 'ok', message: `Connected as ${identity}` };
  } catch (err) {
    return networkOutcome(err, String(token ?? ''));
  }
}

async function probeHiggsfield(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // ENG-8502. Higgsfield cannot reuse `probeBearerJson` for two reasons, both
  // verified against the live API on 2026-08-05:
  //
  //  1. **The scheme is `Key`, not `Bearer`** — `Authorization: Key
  //     KEY_ID:KEY_SECRET` (ENG-8440). A Bearer probe 401s on a perfectly good
  //     credential, which is the worst failure mode available to a health
  //     check: it reports the customer's key as revoked when nothing is wrong.
  //  2. **The success body is a bare JSON ARRAY**, not an object carrying an
  //     identity, so there is no `user` to name. Emptiness is the only signal —
  //     and per ENG-8358's lesson a 200 carrying nothing must not read green,
  //     since an empty array is equally what a stubbed or misrouted endpoint
  //     returns.
  //
  // `/v1/motions` is the right endpoint: read-only, free, and it answers 200
  // even on an account with ZERO credits (verified). That isolates "is the
  // credential good" from "can this account afford to generate" — probing a
  // generation endpoint would conflate them and report a valid key as broken
  // the moment the customer ran out of credit.
  const token = creds.api_key ?? creds.access_token;
  if (!token) return { status: 'down', message: 'No Higgsfield credential present' };
  // A pasted key missing the colon is the likeliest operator error, and the
  // vendor's answer to it is a 401 indistinguishable from a revoked key. Naming
  // it turns "your key was rejected" into "your key is the wrong shape".
  if (!token.includes(':')) {
    return {
      status: 'down',
      message:
        'Higgsfield credential is not in KEY_ID:KEY_SECRET form — re-paste it from cloud.higgsfield.ai/api-keys',
    };
  }
  try {
    const res = await timedFetch(fetchImpl, 'https://platform.higgsfield.ai/v1/motions', {
      headers: { Authorization: `Key ${token}` },
    });
    if (!res.ok) {
      // ENG-5463: the 429 guard that used to sit here now lives in
      // `statusForHttp`, shared by every provider. Like Vercel, this probe does
      // NOT opt in to the 403 rate-limit check — a Higgsfield 403 is a verified
      // rejected key pair (or the out-of-credits case handled just below), and a
      // header-driven downgrade would stop a dead key ever escalating.
      //
      // Higgsfield overloads 403 with "Not enough credits" — an authenticated
      // account that simply cannot generate. Reading that as a dead credential
      // would tell the customer to re-paste a key that is perfectly good.
      // `/v1/motions` is free so it should not arise here, but the vendor owns
      // that behaviour and a future change must not flip a valid key to `down`.
      const body = await res.text().catch(() => '');
      if (res.status === 403 && /credit/i.test(body)) {
        return {
          status: 'degraded',
          message: 'Higgsfield authenticated, but the account is out of credits — generation will fail',
        };
      }
      const message =
        res.status === 429
          ? rateLimitMessage('Higgsfield', res.status)
          : res.status === 401 || res.status === 403
            ? `Higgsfield rejected the key pair (${res.status}) — invalid or revoked API key`
            : `Higgsfield API returned ${res.status}`;
      return {
        status: statusForHttp(res.status),
        cause: causeForHttp(res.status),
        message,
      };
    }
    const motions = (await res.json()) as unknown;
    if (!Array.isArray(motions) || motions.length === 0) {
      return { status: 'down', message: 'Higgsfield returned no motion presets for this key' };
    }
    return { status: 'ok', message: `Higgsfield reachable — ${motions.length} motion presets` };
  } catch (err) {
    return networkOutcome(err, String(token ?? ''));
  }
}

async function probeSportsyear(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // ENG-10524. Sportsyear cannot reuse `probeBearerJson`: it authenticates with
  // HTTP Basic, the API key as the USERNAME and an empty password. The header is
  // built by `basicUsernameAuthorization` — the same function the broker's
  // templater uses — so a green here is a green for the exact header every tool
  // call sends, not for a second encoding that merely resembles it.
  //
  // `GET /api/v1/member` is the endpoint: free, read-only, no arguments, and it
  // returns the authenticated member account, so there is no empty-account case
  // for a good key to fail. Verified against the live API on 2026-09-15 without
  // a valid key: no credentials and a bad key BOTH return `401` with
  // `WWW-Authenticate: Basic realm="Sportsyear API - Member Login Required"` and
  // no rate-limit headers, on `/member` and on `fixtures/search` alike (auth is
  // checked before parameter validation). So a Sportsyear 401 is a dead key, and
  // this probe does NOT opt in to the 403 rate-limit downgrade: the vendor
  // advertises no rate-limit headers, and a header-driven downgrade could only
  // ever mask a dead credential.
  const key = creds.api_key ?? creds.access_token;
  if (!key) return { status: 'down', message: 'No Sportsyear credential present' };
  let authorization: string;
  try {
    authorization = basicUsernameAuthorization(String(key));
  } catch {
    // A pasted `key:` or `email:password` is the likeliest operator error, and
    // the vendor's answer to it would be a 401 indistinguishable from a revoked
    // key. Naming the shape turns "rejected" into "wrong thing pasted".
    return {
      status: 'down',
      message: 'Sportsyear credential is not a bare API key (it contains ":") — paste the key alone, not email:password',
    };
  }
  // The base64 form is as much a credential as the key itself (it decodes to
  // it), so both are redacted from anything we did not author.
  const encoded = authorization.slice('Basic '.length);
  try {
    const res = await timedFetch(fetchImpl, 'https://sportsyear.com.au/api/v1/member', {
      headers: { Authorization: authorization, Accept: 'application/json' },
    });
    if (!res.ok) {
      const message =
        res.status === 429
          ? rateLimitMessage('Sportsyear', res.status)
          : res.status === 401 || res.status === 403
            ? `Sportsyear rejected the API key (${res.status}) — removed, regenerated, or API access disabled on the member account`
            : `Sportsyear API returned ${res.status}`;
      return {
        status: statusForHttp(res.status),
        cause: causeForHttp(res.status),
        message,
      };
    }
    const body = (await res.json()) as unknown;
    const member =
      typeof body === 'object' && body !== null && !Array.isArray(body)
        ? (body as { id?: unknown; organisation?: unknown })
        : null;
    const id = member?.id;
    // ENG-8358: a 200 carrying nothing must not read green. The member payload
    // always carries a numeric `id` for a real account, so its absence means the
    // response is not the member endpoint answering — a proxy, a captive portal,
    // or a changed envelope — and none of those proves the key works.
    if (typeof id !== 'number' && !(typeof id === 'string' && id.trim() !== '')) {
      return { status: 'down', message: 'Sportsyear returned no member account for this API key' };
    }
    // The organisation, not the member's name or email: this message is
    // persisted and rendered in the console, and a person's contact details have
    // no business in a health chip.
    const label =
      typeof member?.organisation === 'string' && member.organisation.trim() !== ''
        ? member.organisation.trim()
        : `member ${String(id)}`;
    // The label is vendor text, so it gets the same redaction as the error path.
    return { status: 'ok', message: `Reached Sportsyear as ${redactSecret(redactSecret(label, String(key)), encoded)}` };
  } catch (err) {
    const outcome = networkOutcome(err, String(key));
    return outcome.message === undefined
      ? outcome
      : { ...outcome, message: redactSecret(outcome.message, encoded) };
  }
}

/**
 * ENG-10608: Instantly API v2. `GET /api/v2/accounts?limit=1` is free,
 * read-only and argument-free, but it needs the `accounts:read` scope, and an
 * Instantly key is minted with a customer-chosen scope set. The setup
 * instructions say a key with fewer scopes still works for the parts it can
 * reach, so a 403 must NOT read as a dead credential.
 *
 * Instantly documents 401 for a missing or invalid key and 403 for a key that
 * lacks the endpoint's scope. So: 401 is `down` (the Higgsfield/Vercel
 * convention), a 403 is `degraded` (authenticated, partially usable: the same
 * grading `probeHiggsfield` gives an authenticated-but-limited 403), 429 and 5xx
 * go through the shared `statusForHttp`.
 */
async function probeInstantly(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  const token = creds.api_key ?? creds.access_token;
  if (!token) return { status: 'down', message: 'No credential present' };
  try {
    const res = await timedFetch(fetchImpl, 'https://api.instantly.ai/api/v2/accounts?limit=1', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 403) {
      return {
        status: 'degraded',
        message:
          'Instantly accepted the key, but it lacks the accounts:read scope - tools needing other scopes may still work',
      };
    }
    if (!res.ok) {
      const message =
        res.status === 401
          ? 'Instantly rejected the key (401) - invalid or revoked API key'
          : res.status === 429
            ? rateLimitMessage('Instantly', res.status)
            : `Instantly API returned ${res.status}`;
      return { status: statusForHttp(res.status), cause: causeForHttp(res.status), message };
    }
    return { status: 'ok', message: 'Connected to Instantly' };
  } catch (err) {
    return networkOutcome(err, String(token));
  }
}

const PROBE_DEFINITIONS = new Set([
  'linear',
  'google-workspace',
  'xero',
  'v0',
  'github',
  'buffer',
  'linkedin-ads',
  // ENG-8422: joins the set once ENG-8421 gave Vercel a customer-supplied API
  // token. Before that there was no server-held credential to check, so the
  // honest verdict really was `unverified`; now a revoked or mistyped token is
  // the most likely failure and the probe is what catches it.
  'vercel',
  // ENG-8502: the same story one issue later. ENG-8440 moved Higgsfield from a
  // host-brokered OAuth MCP to a customer-supplied api_key but did not add it
  // here, so it fell to the `builtin` no-op and reported `unverified` forever.
  // That is not merely uninformative: nothing promotes a row off `configured`
  // without a real verdict, and the console renders `configured` as
  // "not connected" — so a WORKING install told the customer it was broken.
  'higgsfield',
  // ENG-10082: Sprout Social. This entry changes no behaviour today, and saying
  // so is the point — the Test route's guard reads
  // `!isHttpProbeProvider(id) && !descriptor.centralReachable`, and
  // `HTTP_PROBE_PROVIDERS` stamps `centralReachable: true`, so the second
  // conjunct is already false and the guard cannot fire whether or not this
  // entry exists. It is here because `isHttpProbeProvider` is this module's
  // exported answer to "is there a central probe for this definition", and a
  // caller reading it alone would be told no.
  //
  // The divergence that actually bites runs the OTHER way, and was demonstrated
  // rather than assumed: with this entry present and the switch case absent,
  // `isHttpProbeProvider` answers true while `probeHttpProvider` returns null —
  // the predicate promising a probe that does not exist. That is the ENG-5463
  // lesson in the header of the coverage guard (an exemption that merely NAMES
  // the probe it wants) in a new place. ENG-10095 now checks it, in
  // `src/__tests__/eng-10095-probe-registry-parity.test.ts`: every member of
  // this set must reach a real `case`, and every `case` must be a member. Note
  // what that guard deliberately does NOT assert — this set and
  // `HTTP_PROBE_PROVIDERS` are NOT equal (github is here and not there, ENG-6100)
  // and asserting equality would break that on purpose. It asserts the SUBSET
  // that direction allows instead.
  'sprout-social',
  // ENG-10524: Sportsyear — HTTP Basic against `GET /api/v1/member`. See
  // `probeSportsyear` and the `HTTP_PROBE_PROVIDERS` entry for why it ships
  // with the integration rather than after the first silent failure.
  'sportsyear',
  // ENG-10608: Instantly - see `probeInstantly` (a scope-limited 403 grades
  // `degraded`, not `down`).
  'instantly',
]);

/** True when {@link probeHttpProvider} knows how to probe this definition. */
export function isHttpProbeProvider(definitionId: string): boolean {
  return PROBE_DEFINITIONS.has(definitionId);
}

/**
 * Every definition {@link isHttpProbeProvider} answers true for.
 *
 * Exported for the ENG-10095 parity guard. It returns an array rather than the
 * Set so callers cannot mutate the registry, and the guard MUST iterate this
 * accessor — a hand-copied id list in the test rebuilds the very drift the
 * guard exists to catch, and would do so silently.
 */
export function listHttpProbeProviders(): readonly string[] {
  return [...PROBE_DEFINITIONS];
}

/**
 * Run the read-only HTTP probe for a direct-API provider. Returns `null` for a
 * definition this module doesn't know — callers should treat that as "no probe".
 *
 * Since ENG-10095, "doesn't know" and "absent from `PROBE_DEFINITIONS`" are a
 * proven equivalence rather than a convention: the parity guard fails if a
 * member of that set falls through to `default`, or if a `case` here names a
 * definition the set omits. Add a case and its set entry in the same change.
 */
export async function probeHttpProvider(
  definitionId: string,
  credentials: HttpCreds,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectivityProbeOutcome | null> {
  switch (definitionId) {
    case 'linear':
      return probeLinear(credentials, fetchImpl);
    case 'buffer':
      return probeBuffer(credentials, fetchImpl);
    case 'vercel':
      return probeVercel(credentials, fetchImpl);
    case 'higgsfield':
      return probeHiggsfield(credentials, fetchImpl);
    case 'sportsyear':
      return probeSportsyear(credentials, fetchImpl);
    case 'instantly':
      return probeInstantly(credentials, fetchImpl);
    case 'google-workspace':
      return probeBearerJson('https://www.googleapis.com/oauth2/v2/userinfo', credentials, fetchImpl, (body) => {
        const info = body as { name?: string; email?: string };
        return { status: 'ok', message: `Connected as ${info.name ?? info.email ?? 'unknown'}` };
      });
    case 'xero':
      return probeBearerJson('https://api.xero.com/connections', credentials, fetchImpl, (body) => {
        const conns = (body ?? []) as Array<{ tenantName?: string }>;
        if (!conns.length) return { status: 'down', message: 'No Xero organisations connected' };
        return { status: 'ok', message: `Connected to ${conns[0]?.tenantName ?? 'Xero'}` };
      });
    case 'linkedin-ads':
      // OpenID Connect userinfo, not a Marketing API route: it authenticates on
      // the bearer alone, so the probe needs neither the dated `LinkedIn-Version`
      // header nor the RESTli protocol header that every /rest/* call requires
      // (probeBearerJson sends Authorization only). `openid`/`profile`/`email`
      // are in BOTH defaultScopes and readOnlyScopes, so a read-only install
      // probes green too. A 401 here is the honest "reconnect required".
      return probeBearerJson('https://api.linkedin.com/v2/userinfo', credentials, fetchImpl, (body) => {
        const user = body as { name?: string; email?: string };
        return { status: 'ok', message: `Connected as ${user.name ?? user.email ?? 'unknown'}` };
      });
    case 'v0':
      return probeBearerJson('https://api.v0.dev/v1/user', credentials, fetchImpl, (body) => {
        const user = body as { name?: string; email?: string };
        return { status: 'ok', message: `Connected as ${user.name ?? user.email ?? 'unknown'}` };
      });
    case 'sprout-social':
      // ENG-10082. `GET /v1/metadata/client` is the customer-account read that
      // backs `sprout_social_list_clients`. It satisfies every constraint the
      // ticket sets for an endpoint that will run hourly against a customer's
      // live account: free (Sprout meters nothing here; the token's ceiling is
      // 60 req/min and 250k/month, against which an hourly probe is ~720/month),
      // read-only, and no-argument — it is the discovery call every other Sprout
      // tool depends on, so it needs nothing an empty account could fail to
      // supply.
      //
      // Falsifiable, verified against the live API on 2026-09-06 without a
      // valid key: a bad bearer returns `401 {"code":401,"message":"Credentials
      // not valid"}` and a missing one `401 ... "Missing bearer token in
      // request"`. Auth failure is therefore 401, never 403 — so `probeBearerJson`'s
      // shared 403-rate-limit check cannot mask a dead credential here: a Sprout
      // 403 is a plan gate or a permission, and a bare one still reads `down`.
      return probeBearerJson('https://api.sproutsocial.com/v1/metadata/client', credentials, fetchImpl, (body) => {
        // Read endpoints answer `{ "data": [...], "paging": {...} }`.
        //
        // The shape is VALIDATED rather than asserted, because a cast here is
        // unsound in a way that fabricates a green (CodeRabbit, PR #5656): a
        // bare `as Array<…>` followed by a `.length` check is satisfied by any
        // STRING, so a `{"data":"error"}` body — entirely plausible from a
        // proxy, a captive portal, or a future error envelope — would report
        // `ok` with "5 client account(s)" having found no client at all. That
        // is this probe's whole purpose inverted.
        const data = (body as { data?: unknown } | null)?.data;
        const clients = Array.isArray(data)
          ? data.filter((row): row is { customer_id: string | number; name?: string } => {
              if (typeof row !== 'object' || row === null) return false;
              const id = (row as { customer_id?: unknown }).customer_id;
              return typeof id === 'string' || typeof id === 'number';
            })
          : [];
        // ENG-8358: a 200 carrying nothing must not read green. That rule is
        // not merely conventional here — a Sprout token is minted INSIDE a
        // customer account (Settings → Global Features → API), so at least one
        // client is structural rather than a new-account edge case, and every
        // Sprout tool but this one requires a `customer_id` that only this call
        // supplies. Hence the filter is on `customer_id` specifically and not
        // on mere object-ness: a client row without one is as useless to the
        // agent as no row at all, so counting it would green an install that
        // cannot answer a single Sprout query.
        if (!clients.length) {
          return { status: 'down', message: 'Sprout Social returned no usable client account for this token' };
        }
        return {
          status: 'ok',
          message: `Reached Sprout Social — ${clients.length} client account(s), incl. ${clients[0]?.name ?? 'unnamed'}`,
        };
      });
    case 'github':
      // ENG-6100: works for both auth shapes — OAuth access_token and a PAT
      // (api_key) both authenticate `GET /user` as a Bearer token. The
      // User-Agent header is mandatory (GitHub 403s without it). Message
      // states what was OBSERVED ("Reached GitHub as <login>"), not that the
      // agent's tools will work — auth is not the same as token scope (the
      // missing-scopes signal is surfaced separately by the Test route).
      return probeBearerJson('https://api.github.com/user', credentials, fetchImpl, (body) => {
        const u = body as { login?: string; name?: string };
        return { status: 'ok', message: `Reached GitHub as ${u.login ?? u.name ?? 'unknown'}` };
      }, { 'User-Agent': 'augmented-team-connectivity-probe', 'X-GitHub-Api-Version': '2026-03-10' });
    default:
      return null;
  }
}
